import { useSelector } from "react-redux";
import LeagueTeamName from "../../LeagueTeamName";
import ResultDial from "../../ResultDial";
import { boosterIdKey } from "../../../Common/constants";
import { BoosterChips } from "../../BoosterChips";
import { getBoosterCategory } from "../../../Common/utils";
import { getTranslations } from "../../../Common/utils";

/**
 * OVERALL SCOREBOARD LAYOUT REFERENCE GUIDE
 * ---------------------------------------------------------------------------------
 * This component handles OVERALL type scoreboards (team vs team comparisons).
 * All grid columns are defined using fractional units (fr) for fluid scaling.
 *
 * | Layout Type | Image Ref | Description               | layoutConfig                                                  |
 * |-------------|-----------|---------------------------|---------------------------------------------------------------|
 * | Overall 1   | Image 1   | Wide Bar (Text)           | { variant: "inline-header" }                                  |
 * | Overall 2   | Image 2   | Standard Bar (Text)       | { variant: "inline-header", containerClass: "narrow" }        |
 * | Overall 3   | Image 3   | Stacked (Score below)     | { variant: "stacked" }                                        |
 * ---------------------------------------------------------------------------------
 */

/**
 * @param {Object} layoutConfig - Configuration object for layout styling
 * @param {string} layoutConfig.containerClass - Additional CSS class for the container (e.g., "narrow")
 * @param {string} layoutConfig.variant - "inline-header" | "stacked" (Score below names)
 * @param {Object} overallResult - The overall battle result data
 * @param {Array} overallResult.members - Array of two members participating in the battle
 * @param {string} overallResult.overall_battle_result - Result indicator ("W", "L", "D")
 * @param {number} overallResult.overall_battle_result_percentage - Battle result percentage
 */
const OverallScoreboardContainer = ({
  layoutConfig = {},
  overallResult = {},
}) => {
  const gameplayState = useSelector((state) => state.gameplay);
  const { containerClass = "", variant = "inline-header" } = layoutConfig;
  const { selectedBooster } = gameplayState;

  const userState = useSelector((state) => state.user);

  const {
    members = [],
    overall_battle_result = "",
    overall_battle_result_percentage = 0,
  } = overallResult;

  // Find the current user and opponent from members array
  const currentUser = members?.find((member) => member?.is_current_user === 1);
  const opponent = members?.find((member) => member?.is_current_user === 0);

  // Determine result status for the dial
  const resultStatus = overall_battle_result?.toLowerCase() || "";

  return (
    <div
      className={`si-scoreboard si-type-overall si-variant-${variant} ${containerClass}`}
    >
      <div className="si-main-grid">
        {/* LEFT GROUP - Current User's Team */}
        <div className="si-col-team si-left">
          <div className="si-badge">
            <LeagueTeamName
              teamNumber={currentUser?.team_no}
              teamName={"MY TEAM"}
            />
          </div>
          <div className="si-info">
            <div className="si-title">
              {currentUser?.team_name
                ? decodeURIComponent(currentUser.team_name)
                : "--"}
            </div>
            <div className="si-sub">
              {currentUser?.user_name
                ? decodeURIComponent(currentUser.user_name)
                : `${userState?.data?.FirstName} ${userState?.data?.LastName}`}
            </div>
          </div>
        </div>

        {!!currentUser?.booster_id && (
          <div className="si-col-booster si-left">
            <BoosterChips
              boosterCls={
                getBoosterCategory(boosterIdKey[currentUser?.booster_id])
                  ?.boosterCls
              }
              boosterIcon={
                getBoosterCategory(boosterIdKey[currentUser?.booster_id])
                  ?.boosterIcon
              }
            />
          </div>
        )}

        <div className="si-col-score si-left">
          {currentUser?.points?.toFixed(0) ?? "--"}
        </div>

        {/* CENTER DIAL */}
        <div className="si-col-dial">
          <ResultDial
            size="medium"
            percentage={overall_battle_result_percentage || 0}
            status={resultStatus}
            text={overall_battle_result || ""}
          />
        </div>

        <div className="si-col-score si-right">
          {opponent?.points?.toFixed(0) ?? "--"}
        </div>

        {!!opponent?.booster_id && (
          <div className="si-col-booster si-right">
            <BoosterChips
              boosterCls={
                getBoosterCategory(boosterIdKey[opponent?.booster_id])
                  ?.boosterCls
              }
              boosterIcon={
                getBoosterCategory(boosterIdKey[opponent?.booster_id])
                  ?.boosterIcon
              }
            />
          </div>
        )}

        {/* RIGHT GROUP - Opponent's Team */}
        <div className="si-col-team si-right">
          <div className="si-badge">
            <LeagueTeamName
              teamNumber={opponent?.team_no}
              teamName={"OPPONENT"}
              isOpponentTeam={true}
            />
          </div>
          <div className="si-info">
            <div className="si-title">
              {opponent?.team_name
                ? decodeURIComponent(opponent.team_name)
                : getTranslations(
                    "league_hth_opponent_yet_to_join",
                    "Yet to join"
                  )}
            </div>
            <div className="si-sub">
              {opponent?.user_name
                ? decodeURIComponent(opponent.user_name)
                : "--"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverallScoreboardContainer;
